import { LOCALES } from './locales.js';
import { z } from 'zod';

export const APP_ROLES = ['admin', 'makler', 'call_center'] as const;
export const AppRole = z.enum(APP_ROLES);
export type AppRole = z.infer<typeof AppRole>;

export const PROFILE_STATUSES = ['active', 'inactive'] as const;
export const ProfileStatus = z.enum(PROFILE_STATUSES);
export type ProfileStatus = z.infer<typeof ProfileStatus>;

export const CLIENT_TYPES = ['buyer', 'seller', 'interessent'] as const;
export const ClientType = z.enum(CLIENT_TYPES);
export type ClientType = z.infer<typeof ClientType>;

export const CLIENT_STATUSES = [
  'otvoreno',
  'na_cekanju',
  'odbijeno',
  'potpisan_ugovor',
] as const;
export const ClientStatus = z.enum(CLIENT_STATUSES);
export type ClientStatus = z.infer<typeof ClientStatus>;

export const INTEREST_STATUSES = ['none', 'hot', 'warm', 'cold'] as const;
export const InterestStatus = z.enum(INTEREST_STATUSES);
export type InterestStatus = z.infer<typeof InterestStatus>;

export const NEXT_ACTION_TYPES = ['call', 'followup', 'appointment'] as const;
export const NextActionType = z.enum(NEXT_ACTION_TYPES);
export type NextActionType = z.infer<typeof NextActionType>;

export const MARKETING_STAGES = ['lead', 'aufnahme', 'aktiv', 'verkauft'] as const;
export const MarketingStage = z.enum(MARKETING_STAGES);
export type MarketingStage = z.infer<typeof MarketingStage>;

export const APPOINTMENT_TYPES = [
  'aufnahme',
  'besichtigung',
  'kaufanbot',
  'sonstiges',
] as const;
export const AppointmentType = z.enum(APPOINTMENT_TYPES);
export type AppointmentType = z.infer<typeof AppointmentType>;

export const APPOINTMENT_STATUSES = [
  'open',
  'pending',
  'confirmed',
  'completed',
  'cancelled',
  'rejected',
] as const;
export const AppointmentStatus = z.enum(APPOINTMENT_STATUSES);
export type AppointmentStatus = z.infer<typeof AppointmentStatus>;

export const APPOINTMENT_AGENT_ROLES = ['primary', 'secondary', 'call_center'] as const;
export const AppointmentAgentRole = z.enum(APPOINTMENT_AGENT_ROLES);
export type AppointmentAgentRole = z.infer<typeof AppointmentAgentRole>;

export const OFFER_STATUSES = [
  'draft',
  'sent',
  'accepted',
  'rejected',
  'expired',
  'signed',
] as const;
export const OfferStatus = z.enum(OFFER_STATUSES);
export type OfferStatus = z.infer<typeof OfferStatus>;

export const OFFER_PARTICIPANT_ROLES = ['buyer', 'seller'] as const;
export const OfferParticipantRole = z.enum(OFFER_PARTICIPANT_ROLES);
export type OfferParticipantRole = z.infer<typeof OfferParticipantRole>;

export const COMMISSION_TYPES = ['percent', 'fixed'] as const;
export const CommissionType = z.enum(COMMISSION_TYPES);
export type CommissionType = z.infer<typeof CommissionType>;

export const TRANSFER_RESOURCE_TYPES = ['client', 'property'] as const;
export const TransferResourceType = z.enum(TRANSFER_RESOURCE_TYPES);
export type TransferResourceType = z.infer<typeof TransferResourceType>;

export const TRANSFER_STATUSES = ['pending', 'approved', 'rejected'] as const;
export const TransferStatus = z.enum(TRANSFER_STATUSES);
export type TransferStatus = z.infer<typeof TransferStatus>;

export const SECURITY_ALERT_TYPES = [
  'mass_edit',
  'phone_deletion',
  'self_assignment',
  'suspicious_login',
  'ownership_violation',
] as const;
export const SecurityAlertType = z.enum(SECURITY_ALERT_TYPES);
export type SecurityAlertType = z.infer<typeof SecurityAlertType>;

export const ALERT_SEVERITIES = ['critical', 'warning', 'info'] as const;
export const AlertSeverity = z.enum(ALERT_SEVERITIES);
export type AlertSeverity = z.infer<typeof AlertSeverity>;

export const NOTIFICATION_PRIORITIES = ['low', 'medium', 'high'] as const;
export const NotificationPriority = z.enum(NOTIFICATION_PRIORITIES);
export type NotificationPriority = z.infer<typeof NotificationPriority>;

export const SMS_STATUSES = ['pending', 'sent', 'delivered', 'failed'] as const;
export const SmsStatus = z.enum(SMS_STATUSES);
export type SmsStatus = z.infer<typeof SmsStatus>;

export const EMAIL_CAMPAIGN_STATUSES = [
  'draft',
  'scheduled',
  'sending',
  'sent',
  'paused',
] as const;
export const EmailCampaignStatus = z.enum(EMAIL_CAMPAIGN_STATUSES);
export type EmailCampaignStatus = z.infer<typeof EmailCampaignStatus>;

export const EMAIL_SUBSCRIBER_STATUSES = [
  'active',
  'unsubscribed',
  'bounced',
] as const;
export const EmailSubscriberStatus = z.enum(EMAIL_SUBSCRIBER_STATUSES);
export type EmailSubscriberStatus = z.infer<typeof EmailSubscriberStatus>;

export const CONVERSATION_TYPES = ['direct', 'group'] as const;
export const ConversationType = z.enum(CONVERSATION_TYPES);
export type ConversationType = z.infer<typeof ConversationType>;

// Derived from the locale registry so the Zod enum can never drift from what
// the app actually ships. See ./locales.ts — promoting a language to 'live'
// widens this automatically.
export const SUPPORTED_LOCALES = LOCALES.filter((l) => l.status === 'live').map(
  (l) => l.code,
) as [string, ...string[]];
export const Locale = z.enum(SUPPORTED_LOCALES);
export type Locale = z.infer<typeof Locale>;
